import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { MonthNavigator } from '../../components/budget/MonthNavigator';
import { CardSkeleton } from '../../components/common/Skeleton';
import { CategoryShareCard } from '../../components/insights/CategoryShareCard';
import { KpiRow } from '../../components/insights/KpiRow';
import { LimitsPaceCard } from '../../components/insights/LimitsPaceCard';
import { PaceCard } from '../../components/insights/PaceCard';
import { PaceChartCard } from '../../components/insights/PaceChartCard';
import { TopCategoriesCard } from '../../components/insights/TopCategoriesCard';
import { TrendCard } from '../../components/insights/TrendCard';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';
import { useViewPeriod } from '../../lib/useViewPeriod';

const searchSchema = z.object({
  year: z.coerce.number().int().optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
});

export const Route = createFileRoute('/insights/')({
  validateSearch: searchSchema,
  component: InsightsPage,
});

/** Insikter: how this period compares, where the money went, and the longer trend. */
function InsightsPage() {
  usePageTitle('Insikter');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { year, month, ready, max } = useViewPeriod(search);
  const enabled = ready;

  const setPeriod = (y: number, m: number) =>
    void navigate({ to: '/insights', search: { year: y, month: m }, replace: true });

  const summaryQuery = trpc.period.summary.useQuery({ year, month }, { retry: false, enabled });
  const forecastQuery = trpc.insights.monthForecast.useQuery(
    { year, month },
    { retry: false, enabled },
  );
  const overviewQuery = trpc.insights.overview.useQuery({ year, month }, { retry: false, enabled });
  const breakdownQuery = trpc.insights.spendingBreakdown.useQuery(
    { year, month },
    { retry: false, enabled },
  );
  const trendsQuery = trpc.insights.trends.useQuery({ months: 6 }, { retry: false, enabled });

  const summary = summaryQuery.data;
  const forecast = forecastQuery.data;
  const period = summary?.period;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Insikter
        </p>
        <MonthNavigator
          year={year}
          month={month}
          onChange={setPeriod}
          range={period}
          max={max}
          meta={period?.isCurrent ? `dag ${period.daysElapsed} av ${period.daysTotal}` : undefined}
        />
      </div>

      {forecastQuery.error && (
        <p className="text-sm text-negative">
          Kunde inte ladda perioden: {forecastQuery.error.message}
        </p>
      )}

      {forecast ? (
        <>
          <PaceCard forecast={forecast} />
          <KpiRow forecast={forecast} />
        </>
      ) : (
        !forecastQuery.error && <CardSkeleton lines={4} />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        {forecast ? <PaceChartCard forecast={forecast} /> : <CardSkeleton lines={6} />}
        {summary ? (
          <LimitsPaceCard summary={summary} />
        ) : (
          !summaryQuery.error && <CardSkeleton lines={4} />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        {breakdownQuery.isPending ? (
          <CardSkeleton lines={5} />
        ) : (
          <CategoryShareCard breakdown={breakdownQuery.data} />
        )}
        {overviewQuery.isPending ? (
          <CardSkeleton lines={6} />
        ) : (
          <TopCategoriesCard overview={overviewQuery.data} year={year} month={month} />
        )}
      </div>

      {trendsQuery.isPending ? <CardSkeleton lines={6} /> : <TrendCard trends={trendsQuery.data} />}
    </div>
  );
}
